/**
 * Les pointages des techniciens vus au BUREAU (registre B34) : le technicien ne saisit plus ses heures, il pointe (je pars, je
 * suis arrivé, pause, reprendre, je repars, je suis rentré) ; le serveur calcule trajet, temps sur site et pauses ; le bureau
 * vérifie et corrige. Ici : libellés, heures affichées, phrases, lecture d'une heure saisie. Logique pure, testée sans navigateur.
 */
import type { DestinationPointage, EvenementPointage, GestePointage, PointagesJour } from '../api/client';
import { dureeFr, jourFr } from './planning';

export const GESTES_BUREAU: readonly { readonly geste: GestePointage; readonly libelle: string }[] = [
  { geste: 'DEPART', libelle: 'Je pars' },
  { geste: 'ARRIVAL', libelle: 'Je suis arrivé' },
  { geste: 'PAUSE', libelle: 'Pause' },
  { geste: 'RESUME', libelle: 'Reprise de l’intervention' },
  { geste: 'LEAVE', libelle: 'Je quitte le site' },
  { geste: 'HOME', libelle: 'Je suis rentré' },
];

const DESTINATIONS: Readonly<Record<DestinationPointage, string>> = {
  NEXT_CLIENT: 'chez le client suivant',
  WORKSHOP: 'à l’atelier',
  HOME: 'chez lui',
};

/** « Je repars à l’atelier (travail fini) », « Pause »… */
export function libellePointage(e: Pick<EvenementPointage, 'geste' | 'destination' | 'travailFini'>): string {
  const base = GESTES_BUREAU.find((g) => g.geste === e.geste)?.libelle ?? e.geste;
  if (e.geste !== 'LEAVE') return base;
  const ou = e.destination === null ? '' : ` → ${DESTINATIONS[e.destination]}`;
  const fin = e.travailFini === null ? '' : e.travailFini ? ' (travail fini)' : ' (à reprendre)';
  return `${base}${ou}${fin}`;
}

/** L'heure locale d'un instant : « 08:10 ». */
export function heureLocale(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** « 8 h 10 » (pour une phrase). */
export function heureFr(iso: string): string {
  const [h, m] = heureLocale(iso).split(':');
  return `${Number(h)} h ${m}`;
}

/**
 * Lit une heure saisie (« 8:10 », « 08h10 », « 8 h 10 », « 8 ») un jour donné et rend l'instant ISO (heure locale du poste),
 * ou `null` si elle est illisible.
 */
export function instantDepuisHeure(jour: string, saisie: string): string | null {
  const m = /^\s*(\d{1,2})\s*(?:[:hH]\s*(\d{2})?)?\s*$/.exec(saisie);
  if (!m || !/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null;
  const h = Number(m[1]);
  const mn = m[2] === undefined ? 0 : Number(m[2]);
  if (h > 23 || mn > 59) return null;
  const [a, mo, j] = jour.split('-').map(Number) as [number, number, number];
  return new Date(a, mo - 1, j, h, mn, 0, 0).toISOString();
}

/** « 2 saisis après coup », « 1 saisi après coup », vide sans. */
export function phraseSaisiesApresCoup(n: number): string {
  if (n <= 0) return '';
  return n === 1 ? '1 saisi après coup' : `${n} saisis après coup`;
}

/** Une ligne du tableau « Pointages » (écran 7 de l'ébauche). */
export interface LignePointagesTableau {
  readonly cle: string;
  readonly jour: string;
  readonly technicien: string;
  readonly trajet: string;
  readonly surSite: string;
  readonly pause: string;
  readonly moFacturee: string;
  readonly pointages: string;
}

const duree = (m: number): string => (m === 0 ? '—' : dureeFr(m));

export function lignesTableau(pointages: readonly PointagesJour[]): LignePointagesTableau[] {
  return pointages.map((p) => {
    const apresCoup = phraseSaisiesApresCoup(p.saisiesApresCoup);
    return {
      cle: `${p.jour}|${p.technicien.id}`,
      jour: jourFr(p.jour),
      technicien: p.technicien.nom,
      trajet: duree(p.durees.trajetAller + p.durees.trajetRetour),
      surSite: duree(p.durees.surSite),
      pause: duree(p.durees.pause),
      moFacturee: duree(p.minutesFacturees),
      pointages: `${p.evenements.length}${apresCoup === '' ? '' : ` · ${apresCoup}`}`,
    };
  });
}

/** Les membres des jours passés ou d'aujourd'hui (on ne pointe pas un jour à venir), dans l'ordre des jours puis de l'équipe. */
export function membresPointables(
  jours: readonly { readonly day: string; readonly members: readonly { readonly technicianId: string; readonly name: string }[] }[],
  aujourdhui: string,
): { jour: string; technicienId: string; nom: string }[] {
  return jours.filter((j) => j.day <= aujourdhui).flatMap((j) => j.members.map((m) => ({ jour: j.day, technicienId: m.technicianId, nom: m.name })));
}

/** Ce qui empêche d'ajouter un pointage oublié (message en français), ou `null`. */
export function problemeAjout(saisie: { jour: string; technicienId: string; geste: GestePointage | ''; heure: string; destination: DestinationPointage | ''; travailFini: '' | 'oui' | 'non' }): string | null {
  if (saisie.jour === '' || saisie.technicienId === '') return 'Choisissez le jour et le technicien.';
  if (saisie.geste === '') return 'Choisissez le geste.';
  if (instantDepuisHeure(saisie.jour, saisie.heure) === null) return 'Écrivez l’heure comme 8:10 ou 8h10.';
  if (saisie.geste === 'LEAVE' && (saisie.destination === '' || saisie.travailFini === '')) {
    return '« Je quitte le site » : dites où il va et si le travail est fini.';
  }
  if (saisie.geste === 'LEAVE' && saisie.destination === 'NEXT_CLIENT') {
    return 'Un départ chez le client suivant se pointe depuis le téléphone (il ouvre le trajet de l’intervention suivante).';
  }
  return null;
}
